import { DATE_RANGE_FILTER_OPTIONS, VALID_DATE_RANGE_KEYS } from 'src/services/consts/app.const'
import { REGULATION_FILTER_OPTIONS } from 'src/services/consts/banner/behaviour.const'
import {
  BANNERS_COOKIE_CLASSIFICATION_FILTER_OPTIONS,
  BANNERS_DEFAULT_FILTER,
  VALID_COOKIE_CLASSIFICATION_KEYS,
} from 'src/services/consts/banners/list.const'
import { TRACKER_CLASSIFICATION_STATUS } from 'src/services/consts/shared.const'
import { getCutoffDate, getFilterFromQuery } from 'src/services/helpers/util.helper'

/**
 * @typedef {Object} BannerListItem
 * @property {string} siteId
 * @property {string} url
 * @property {string|null} siteName
 * @property {string|null} description
 * @property {string[]} regulations - BANNER_REGULATION values
 * @property {string|null} lastScanned
 * @property {string|null} createdAt
 * @property {number|null} totalItems
 * @property {number|null} unclassifiedItems
 */

/**
 * @typedef {Object} BannerListFilter
 * @property {string} search
 * @property {string[]} regulations
 * @property {string|null} cookieClassification
 * @property {string|null} dateRange
 */

/** Parse URL query into the in-memory filter shape (regulations as `string[]`). */
export function parseBannerFilterFromQuery(query) {
  const raw = getFilterFromQuery({ ...BANNERS_DEFAULT_FILTER, regulations: null }, query)
  const regulationsRaw = raw.regulations
  const regulations = Array.isArray(regulationsRaw)
    ? regulationsRaw
        .flatMap((value) => (typeof value === 'string' ? value.split(',') : []))
        .filter(Boolean)
    : typeof regulationsRaw === 'string'
      ? regulationsRaw.split(',').filter(Boolean)
      : []

  return {
    ...raw,
    regulations,
    dateRange: raw.dateRange && VALID_DATE_RANGE_KEYS.has(raw.dateRange) ? raw.dateRange : null,
    cookieClassification:
      raw.cookieClassification && VALID_COOKIE_CLASSIFICATION_KEYS.has(raw.cookieClassification)
        ? raw.cookieClassification
        : null,
  }
}

/** @returns {BannerListItem|null} */
export function normalizeBannerListItem(raw) {
  if (!raw || typeof raw !== 'object' || !raw.siteId) return null
  return {
    siteId: raw.siteId,
    url: raw.url,
    siteName: raw.siteName ?? null,
    description: raw.description ?? null,
    regulations: Array.isArray(raw.regulations) ? raw.regulations : [],
    lastScanned: raw.lastScanned ?? null,
    createdAt: raw.createdAt ?? null,
    totalItems: raw.totalItems ?? null,
    unclassifiedItems: raw.unclassifiedItems ?? null,
  }
}

/**
 * Client-side filter for the banners list table.
 * @param {BannerListItem[]} banners
 * @param {BannerListFilter} filterBy
 * @returns {BannerListItem[]}
 */
export function filterBanners(banners, { search, regulations, cookieClassification, dateRange }) {
  if (!Array.isArray(banners)) return []

  const trimmedSearch = search?.trim() ?? ''
  const searchRegex = trimmedSearch
    ? new RegExp(trimmedSearch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i')
    : null

  const regulationList = regulations?.length ? regulations : null

  const cutoff = dateRange && VALID_DATE_RANGE_KEYS.has(dateRange) ? getCutoffDate(dateRange) : null
  const cutoffTime = cutoff ? cutoff.getTime() : null

  return banners.filter((banner) => {
    const matchesSearch =
      !searchRegex || searchRegex.test(banner.siteName ?? '') || searchRegex.test(banner.url ?? '')

    if (!matchesSearch) {
      return false
    }

    if (regulationList && !banner.regulations?.some((r) => regulationList.includes(r))) {
      return false
    }

    if (
      cookieClassification === TRACKER_CLASSIFICATION_STATUS.UNCLASSIFIED &&
      !(banner.unclassifiedItems > 0)
    ) {
      return false
    }

    if (
      cookieClassification === TRACKER_CLASSIFICATION_STATUS.CLASSIFIED &&
      banner.unclassifiedItems !== 0
    ) {
      return false
    }

    if (cutoffTime) {
      const lastScanned = banner.lastScanned ? new Date(banner.lastScanned).getTime() : null
      if (!lastScanned || Number.isNaN(lastScanned) || lastScanned < cutoffTime) {
        return false
      }
    }

    return true
  })
}

/**
 * Builds regulation filter options with counts from a pre-filtered banner list.
 * @param {BannerListItem[]} banners
 * @returns {{ value: string, label: string, icon?: string, iconSrc?: string, count: number }[]}
 */
export function getBannerRegulationFilterOptions(banners) {
  const list = Array.isArray(banners) ? banners : []

  return REGULATION_FILTER_OPTIONS.map(({ value, label, icon, iconSrc }) => {
    let count = 0
    for (const banner of list) {
      if (banner.regulations?.includes(value)) {
        count += 1
      }
    }
    return { value, label, icon, iconSrc, count }
  })
}

/**
 * Builds date range filter options with counts from a pre-filtered banner list.
 * @param {BannerListItem[]} banners
 * @returns {{ value: string|null, label: string, icon: string, count: number }[]}
 */
export function getBannerDateRangeFilterOptions(banners) {
  const list = Array.isArray(banners) ? banners : []

  return DATE_RANGE_FILTER_OPTIONS.map(({ value, label, icon }) => {
    if (!value) {
      return { value, label, icon, count: list.length }
    }

    const cutoff = getCutoffDate(value)
    if (!cutoff) {
      return { value, label, icon, count: 0 }
    }

    const cutoffTime = cutoff.getTime()
    let count = 0
    for (const banner of list) {
      if (!banner?.lastScanned) continue
      const lastScannedTime = new Date(banner.lastScanned).getTime()
      if (!Number.isNaN(lastScannedTime) && lastScannedTime >= cutoffTime) {
        count += 1
      }
    }

    return { value, label, icon, count }
  })
}

/**
 * Builds cookie-classification filter options with counts from a pre-filtered banner list.
 * @param {BannerListItem[]} banners
 * @returns {{ value: string|null, label: string, icon: string, count: number }[]}
 */
export function getBannerCookieClassificationFilterOptions(banners) {
  const list = Array.isArray(banners) ? banners : []

  return BANNERS_COOKIE_CLASSIFICATION_FILTER_OPTIONS.map(({ value, label, icon }) => {
    if (!value) {
      return { value, label, icon, count: list.length }
    }

    let count = 0
    for (const banner of list) {
      if (
        (value === TRACKER_CLASSIFICATION_STATUS.UNCLASSIFIED && banner.unclassifiedItems > 0) ||
        (value === TRACKER_CLASSIFICATION_STATUS.CLASSIFIED && banner.unclassifiedItems === 0)
      ) {
        count += 1
      }
    }

    return { value, label, icon, count }
  })
}
